import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import { Button } from '../../components/ui/button';
import { CenteredSpinner } from '../../components/ui/Spinner';
import type { ValidateIssue } from '../../lib/publishing/validateWarnings';
import type { PostDestinationId } from '../../lib/publishing/destinations';
import { isPostDeletable } from '../../lib/publishing/postDelete';
import {
  ACCOUNT_CHANGED_WARNING,
  shouldWarnAccountChanged,
} from '../../lib/publishing/wildcardInsert';
import {
  createPost,
  deletePost,
  getAccountPlatforms,
  getPost,
  listPublishingAccounts,
  updatePost,
  validatePost,
} from './api';
import { ConfirmDeletePostModal } from './ConfirmDeletePostModal';
import {
  effectiveCaption,
  emptyComposerState,
  enabledTargets,
  fromPost,
  isReadOnlyStatus,
  selectedDestinationIds,
  toPostInput,
  type ComposerState,
} from './composerState';
import { AccountCombobox } from './AccountCombobox';
import { AccountLinkIcons } from '../../components/ui/AccountLinkIcons';
import { CaptionBox } from './CaptionBox';
import { CaptionWorkspace, CaptionWorkspaceTrigger } from './CaptionWorkspace';
import { DestinationPicker } from './DestinationPicker';
import { DestinationOptionsSection } from './DestinationOptionsSection';
import { FrozenCaptionNotice } from './FrozenCaptionNotice';
import { MediaPicker } from './MediaPicker';
import { PublishBar } from './PublishBar';
import { PostPreviews } from './previews/PostPreviews';

// Publishing composer (redesigned 2026-07-20, modelled on Agorapulse).
//
// The flow top-to-bottom: account → tick the DESTINATIONS → caption (emoji +
// live per-platform counters) → media → one collapsed options block per ticked
// destination → dispatch (draft / schedule / publish now / queue), with the
// schedule offering both an apply-to-all time and per-destination overrides.
//
// Published posts are read-only (duplicate to repost).

export function ComposerPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();

  const accounts = useQuery({ queryKey: ['publishing-accounts'], queryFn: listPublishingAccounts });
  const existing = useQuery({
    queryKey: ['publishing-post', id],
    queryFn: () => getPost(id!),
    enabled: isEdit,
  });

  const [state, setState] = useState<ComposerState>(() => emptyComposerState());
  const [hydrated, setHydrated] = useState(!isEdit);
  // Resolve-on-insert leaves LITERAL account details in the caption, so switching
  // account makes them potentially wrong. We don't re-resolve (that would mean
  // tracking which spans came from where, and it breaks the moment the AM edits
  // one) — the text is visible, so a one-off note is enough to get it checked.
  const [accountChangedNote, setAccountChangedNote] = useState(false);
  // The AI drafting workspace is a full-width region ABOVE the composer grid, so
  // its open state lives here rather than inside the trigger.
  const [workspaceOpen, setWorkspaceOpen] = useState(false);

  // Hydrate from the loaded post once.
  useEffect(() => {
    if (isEdit && existing.data && !hydrated) {
      setState(fromPost(existing.data));
      setHydrated(true);
    }
  }, [isEdit, existing.data, hydrated]);

  // New-post deep-link: /publishing/new?account=<id>&date=<YYYY-MM-DD>. The
  // account pre-selects the calendar's scoped account (the onboarding ?account=:id
  // precedent), ONLY if it's actually in the postable set — a connected-but-not-
  // content_creation account is silently ignored rather than leaving the picker in
  // a dead state. The date (from the per-day calendar "+" icon) switches to
  // Schedule mode pre-filled to that day at 9am (adjustable). Applied ONCE.
  const [searchParams] = useSearchParams();
  const preselectAccount = isEdit ? null : searchParams.get('account');
  const preselectDate = isEdit ? null : searchParams.get('date');
  const [preselectApplied, setPreselectApplied] = useState(false);
  useEffect(() => {
    if (isEdit || preselectApplied || !accounts.data) return;
    if (!preselectAccount && !preselectDate) {
      setPreselectApplied(true);
      return;
    }
    setState((s) => {
      // A valid preselected account starts a fresh composition for it; otherwise
      // keep the current (empty) account.
      const accountValid = !!preselectAccount && accounts.data!.some((a) => a.id === preselectAccount);
      let next = accountValid
        ? { ...emptyComposerState(preselectAccount!), publishMode: s.publishMode }
        : s;
      if (preselectDate) {
        const iso = dateParamToScheduledIso(preselectDate);
        if (iso) next = { ...next, publishMode: 'schedule', scheduledFor: iso };
      }
      return next;
    });
    setPreselectApplied(true);
  }, [isEdit, preselectApplied, preselectAccount, preselectDate, accounts.data]);

  const readOnly = isEdit && existing.data ? isReadOnlyStatus(existing.data.status) : false;

  const platforms = useQuery({
    queryKey: ['publishing-account-platforms', state.kyckAccountId],
    queryFn: () => getAccountPlatforms(state.kyckAccountId),
    enabled: state.kyckAccountId !== '',
  });

  // Debounced live validation (platform rules + Zernio warnings) for the previews.
  const [issues, setIssues] = useState<ValidateIssue[]>([]);
  const input = useMemo(() => toPostInput(state), [state]);
  useEffect(() => {
    if (input.targets.length === 0) {
      setIssues([]);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(() => {
      validatePost(input)
        .then((res) => !cancelled && setIssues(res))
        .catch(() => !cancelled && setIssues([]));
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [input]);

  const save = useMutation({
    mutationFn: () => (isEdit ? updatePost(id!, input) : createPost(input)),
    onSuccess: (res) => {
      if (res.dispatchError) {
        toast.warning(`Saved as a draft — publishing failed: ${res.dispatchError}`);
      } else {
        toast.success(labelForMode(state.publishMode, isEdit));
      }
      navigate('/publishing');
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Could not save the post'),
  });

  // Delete a persisted draft / scheduled post. The modal carries the error so a
  // Zernio schedule-cancel failure stays visible instead of dismissing.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const remove = useMutation({
    mutationFn: () => deletePost(id!),
    onSuccess: () => {
      setConfirmDelete(false);
      toast.success('Post deleted');
      navigate('/publishing');
    },
  });
  // Only a persisted, non-published post can be deleted (published/partial/
  // publishing hide the button; the server rejects them as a backstop).
  const canDelete = isEdit && !!existing.data && isPostDeletable(existing.data.status);
  const deletingScheduled =
    !!existing.data &&
    (existing.data.status === 'scheduled' || existing.data.zernioPostId !== null);

  if (isEdit && existing.isLoading) return <CenteredSpinner />;

  const connected = platforms.data?.connected ?? {
    facebook: false,
    instagram: false,
    googlebusiness: false,
  };

  const selected = selectedDestinationIds(state);
  const selectedTargets = enabledTargets(state);
  const canSubmit = state.kyckAccountId !== '' && selected.length > 0 && !readOnly;

  const toggleDestination = (dest: PostDestinationId, enabled: boolean) =>
    setState((s) => ({
      ...s,
      targets: { ...s.targets, [dest]: { ...s.targets[dest], enabled } },
    }));

  // Changing the account still resets the destinations/media/schedule (those are
  // account-specific), but CARRIES THE CAPTION over — losing typed copy because
  // the AM corrected the account is a bad trade, and since resolve-on-insert
  // leaves literal details in that text, the carried caption is exactly what the
  // note below is about.
  const handleAccountChange = (kyckAccountId: string) => {
    const carried = state.content;
    if (shouldWarnAccountChanged(state.kyckAccountId, kyckAccountId, carried)) {
      setAccountChangedNote(true);
    }
    setState((s) => ({
      ...emptyComposerState(kyckAccountId),
      publishMode: s.publishMode,
      content: carried,
    }));
  };

  return (
    <div>
      <PageHeader
        title={isEdit ? 'Edit post' : 'New post'}
        description={readOnly ? 'This post is published and read-only.' : undefined}
        backHref="/publishing"
        actions={
          canDelete ? (
            <Button variant="outline" onClick={() => setConfirmDelete(true)}>
              Delete
            </Button>
          ) : undefined
        }
      />

      {workspaceOpen && !readOnly && (
        <CaptionWorkspace
          kyckAccountId={state.kyckAccountId}
          destinations={selected}
          media={state.media}
          currentCaption={state.content}
          onUse={(content) => setState((s) => ({ ...s, content }))}
          onClose={() => setWorkspaceOpen(false)}
        />
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_420px]">
        {/* composer column */}
        <div className="space-y-5">
          <div>
            <div className="mb-1 flex items-center gap-2">
              <label className="block text-sm font-medium text-slate-700">Account</label>
              {/* The selected account's external links — check the page or site
                  you're posting for without leaving the composer (new tab). */}
              <AccountLinkIcons
                links={accounts.data?.find((a) => a.id === state.kyckAccountId)?.links}
              />
            </div>
            {accounts.isLoading ? (
              <CenteredSpinner />
            ) : (
              <AccountCombobox
                accounts={accounts.data ?? []}
                value={state.kyckAccountId}
                disabled={readOnly || isEdit}
                onChange={handleAccountChange}
              />
            )}
          </div>

          {state.kyckAccountId !== '' && (
            <>
              {/* §A — destinations sit ABOVE the caption: what you're posting to
                  frames what you write. */}
              <DestinationPicker
                selected={selected}
                connected={connected}
                disabled={readOnly}
                onToggle={toggleDestination}
              />

              {/* §B — wildcardMode="resolved": ONE account, so a wildcard inserts
                  its real text (WYSIWYG, editable in place). The multi-account
                  composer stays on tokens. */}
              <FrozenCaptionNotice targets={state.targets} />
              <CaptionBox
                value={state.content}
                selected={selected}
                effectiveCaptionFor={(dest) => effectiveCaption(state, dest)}
                wildcardMode="resolved"
                wildcardContext={platforms.data?.wildcard ?? null}
                wildcardAccountId={state.kyckAccountId || null}
                disabled={readOnly}
                onChange={(content) => setState((s) => ({ ...s, content }))}
              />
              {/* The trigger sits under the caption it writes into; the
                  WORKSPACE itself renders full-width above the composer grid,
                  because several-hundred-word drafts cannot be read or compared
                  in a 400px column. ON-DEMAND ONLY — it generates on the AM's
                  click, never on mount (captionDraftOnDemandOnly.test.ts). */}
              {!readOnly && (
                <CaptionWorkspaceTrigger
                  kyckAccountId={state.kyckAccountId}
                  destinations={selected}
                  open={workspaceOpen}
                  onOpen={() => {
                    setWorkspaceOpen(true);
                    // The workspace opens above the composer, so bring it into
                    // view. In the click handler, never an effect.
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                />
              )}
              {accountChangedNote && (
                <p className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  <span className="flex-1">{ACCOUNT_CHANGED_WARNING}</span>
                  <button
                    type="button"
                    className="shrink-0 font-medium underline"
                    onClick={() => setAccountChangedNote(false)}
                  >
                    Dismiss
                  </button>
                </p>
              )}

              {/* §C */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Media</label>
                <MediaPicker
                  media={state.media}
                  disabled={readOnly}
                  onChange={(media) => setState((s) => ({ ...s, media }))}
                />
              </div>

              {/* §D — one collapsed options block per TICKED destination, with the
                  two stories consolidated into one block when both are ticked. */}
              {selected.length > 0 && (
                <DestinationOptionsSection
                  targets={state.targets}
                  baseCaption={state.content}
                  disabled={readOnly}
                  onChange={(targets) => setState((s) => ({ ...s, targets }))}
                />
              )}

              {!readOnly && (
                <PublishBar
                  state={state}
                  targets={selectedTargets}
                  profileId={platforms.data?.profileId ?? null}
                  onChange={setState}
                  onSubmit={() => save.mutate()}
                  submitting={save.isPending}
                  canSubmit={canSubmit}
                />
              )}
            </>
          )}
        </div>

        {/* preview column */}
        <div className="lg:sticky lg:top-6 self-start">
          <PostPreviews
            state={state}
            platforms={platforms.data ?? null}
            issues={issues}
            wildcard={platforms.data?.wildcard ?? null}
          />
        </div>
      </div>

      {confirmDelete && (
        <ConfirmDeletePostModal
          scheduled={deletingScheduled}
          busy={remove.isPending}
          errorText={remove.error instanceof Error ? remove.error.message : null}
          onConfirm={() => remove.mutate()}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}

function labelForMode(mode: ComposerState['publishMode'], isEdit: boolean): string {
  switch (mode) {
    case 'now':
      return 'Publishing now';
    case 'schedule':
      return 'Post scheduled';
    case 'queue':
      return 'Added to the queue';
    default:
      return isEdit ? 'Draft saved' : 'Draft created';
  }
}

// The ?date=YYYY-MM-DD deep-link → an ISO instant at a sensible default time
// (09:00 LOCAL, the AM can adjust) for the composer's Schedule mode. Built in
// local time so it round-trips through the datetime-local input as 9am. Returns
// null for a malformed or non-existent date (e.g. 2026-13-40). Exported for tests.
export function dateParamToScheduledIso(date: string, hour = 9): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return null;
  const [, y, mo, d] = m;
  const year = Number(y);
  const month = Number(mo);
  const day = Number(d);
  const dt = new Date(year, month - 1, day, hour, 0, 0, 0);
  // Reject a rolled-over date (Date normalises 2026-13-40 rather than erroring).
  if (
    Number.isNaN(dt.getTime()) ||
    dt.getFullYear() !== year ||
    dt.getMonth() !== month - 1 ||
    dt.getDate() !== day
  ) {
    return null;
  }
  return dt.toISOString();
}
